'use client'

// SUMMARY: Landing-page hero chat demo — fable-style prompt box with 3 free prompts, then a sign-up gate

import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { ArrowRight, ArrowUp, Loader2, Lock, MessageCircle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DemoChatMessage } from '@/components/landing/DemoChatMessage'
import type { ChatMessage as ChatMessageType } from '@/components/chat/use-chat'

const DEMO_PROMPT_LIMIT = 3
// Mirrors the server's httpOnly cookie so the gate survives reloads without an extra request
const REMAINING_STORAGE_KEY = 'euro-ai-demo-remaining'

export function HeroChatDemo() {
	const t = useTranslations('demo')
	const locale = useLocale()
	const { data: session } = useSession()
	const isAdmin = session?.user?.isAdmin ?? false
	const [messages, setMessages] = useState<ChatMessageType[]>([])
	const [draft, setDraft] = useState('')
	const [isStreaming, setIsStreaming] = useState(false)
	const [remaining, setRemaining] = useState(DEMO_PROMPT_LIMIT)
	const [error, setError] = useState(false)
	const [isResetting, setIsResetting] = useState(false)

	const messagesRef = useRef<ChatMessageType[]>([])
	const scrollRef = useRef<HTMLDivElement>(null)

	const gated = remaining <= 0

	useEffect(() => {
		const stored = localStorage.getItem(REMAINING_STORAGE_KEY)
		if (stored !== null) {
			const value = parseInt(stored, 10)
			if (Number.isFinite(value)) setRemaining(Math.max(0, Math.min(DEMO_PROMPT_LIMIT, value)))
		}
	}, [])

	useEffect(() => {
		scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
	}, [messages])

	const syncMessages = (next: ChatMessageType[]) => {
		messagesRef.current = next
		setMessages(next)
	}

	const updateRemaining = (next: number) => {
		setRemaining(next)
		localStorage.setItem(REMAINING_STORAGE_KEY, String(next))
	}

	// Admin-only: wipe the server cookie + IP backstop, then restore the local gate to full
	const resetDemo = async () => {
		if (isResetting) return
		setIsResetting(true)
		try {
			const res = await fetch('/api/ai/demo/reset', { method: 'POST' })
			if (!res.ok) return
			syncMessages([])
			setError(false)
			updateRemaining(DEMO_PROMPT_LIMIT)
		} finally {
			setIsResetting(false)
		}
	}

	// ===============================
	// SECTION: SEND & STREAM
	// ===============================

	const send = useCallback(
		async (text: string) => {
			const trimmed = text.trim()
			if (!trimmed || isStreaming || gated) return

			setError(false)
			setDraft('')
			const history = messagesRef.current.map((m) => ({ role: m.role, content: m.content }))
			const userMessage: ChatMessageType = { id: crypto.randomUUID(), role: 'user', content: trimmed }
			const assistantId = crypto.randomUUID()
			syncMessages([
				...messagesRef.current,
				userMessage,
				{ id: assistantId, role: 'assistant', content: '', streaming: true },
			])
			setIsStreaming(true)

			try {
				const res = await fetch('/api/ai/demo', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						message: trimmed,
						history,
						locale,
						timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					}),
				})

				// Free prompts used up (possibly in another tab): roll back the optimistic turn and gate
				if (res.status === 429) {
					syncMessages(messagesRef.current.filter((m) => m.id !== userMessage.id && m.id !== assistantId))
					updateRemaining(0)
					return
				}
				if (!res.ok || !res.body) throw new Error(`Request failed (${res.status})`)

				await consumeStream(res.body, assistantId)
			} catch {
				setError(true)
				syncMessages(messagesRef.current.filter((m) => m.id !== assistantId || m.content !== ''))
			} finally {
				syncMessages(messagesRef.current.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)))
				setIsStreaming(false)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isStreaming, gated, locale]
	)

	const consumeStream = async (body: ReadableStream<Uint8Array>, assistantId: string) => {
		const reader = body.getReader()
		const decoder = new TextDecoder()
		let buffer = ''

		while (true) {
			const { done, value } = await reader.read()
			if (done) break
			buffer += decoder.decode(value, { stream: true })
			const lines = buffer.split('\n')
			buffer = lines.pop() || ''
			for (const line of lines) {
				if (!line.startsWith('data: ')) continue
				const payload = line.slice(6).trim()
				if (!payload) continue
				try {
					handleEvent(JSON.parse(payload), assistantId)
				} catch {
					/* skip malformed event */
				}
			}
		}
	}

	const handleEvent = (event: Record<string, unknown>, assistantId: string) => {
		switch (event.type) {
			case 'remaining':
				updateRemaining(event.remaining as number)
				break
			case 'status':
				// Transient web-search progress label, shown until the first text chunk arrives
				syncMessages(
					messagesRef.current.map((m) => (m.id === assistantId ? { ...m, status: event.content as string } : m))
				)
				break
			case 'text':
				syncMessages(
					messagesRef.current.map((m) =>
						m.id === assistantId ? { ...m, content: m.content + (event.content as string), status: undefined } : m
					)
				)
				break
			case 'error':
				setError(true)
				break
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			send(draft)
		}
	}

	// ===============================
	// SECTION: RENDER
	// ===============================

	const suggestions = [t('suggestions.0'), t('suggestions.1'), t('suggestions.2')]

	const active = messages.length > 0

	return (
		// Rests slightly narrower and expands (width + messages area) once a conversation starts
		<div
			className={`mx-auto w-full transition-[max-width] duration-500 ease-out ${active ? 'max-w-4xl' : 'max-w-2xl'}`}
		>
			<div className="rounded-2xl border border-border/50 bg-background/60 p-2 shadow-[0_8px_60px_rgba(37,99,235,0.12)] backdrop-blur-md transition-colors focus-within:border-border">
				<div
					ref={scrollRef}
					className={`space-y-4 overflow-y-auto px-3 text-left transition-all duration-500 ease-out ${
						active ? 'mb-3 max-h-[28rem] pb-2 pt-3 opacity-100' : 'max-h-0 opacity-0'
					}`}
				>
					{messages.map((msg) => (
						<DemoChatMessage key={msg.id} message={msg} />
					))}
				</div>

				{gated ? (
					<div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
						<div className="flex items-center gap-2 text-sm font-medium">
							<Lock className="h-3.5 w-3.5 text-blue-600" />
							{t('limitTitle')}
						</div>
						<p className="text-xs text-muted-foreground">{t('limitSubtitle')}</p>
						<Link href="/auth/register">
							<Button size="sm" className="mt-1">
								{t('limitCta')}
								<ArrowRight className="ml-1.5 h-3.5 w-3.5" />
							</Button>
						</Link>
					</div>
				) : (
					<>
						<textarea
							value={draft}
							onChange={(e) => setDraft(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={t('placeholder')}
							maxLength={2000}
							rows={messages.length === 0 ? 3 : 1}
							className="w-full resize-none rounded-xl border border-border/60 bg-transparent px-4 py-3 text-base placeholder:text-muted-foreground transition-colors focus:border-border focus:outline-none"
						/>
						<div className="flex items-center justify-between px-3 pb-2">
							<span className="flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
								<MessageCircle className="h-3.5 w-3.5" />
								{t('remaining', { remaining, limit: DEMO_PROMPT_LIMIT })}
							</span>
							<Button
								size="icon"
								onClick={() => send(draft)}
								disabled={isStreaming || !draft.trim()}
								className="h-9 w-9 rounded-xl"
								aria-label={t('sendLabel')}
							>
								{isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
							</Button>
						</div>
					</>
				)}
			</div>

			{isAdmin && (
				<div className="mt-3 flex justify-center">
					<Button
						size="sm"
						variant="outline"
						onClick={resetDemo}
						disabled={isResetting || isStreaming}
						className="gap-1.5 text-xs"
						title={t('resetTitle')}
					>
						{isResetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
						{t('resetLabel')}
					</Button>
				</div>
			)}

			{error && <p className="mt-3 text-center text-sm text-destructive">{t('error')}</p>}

			{messages.length === 0 && !gated && (
				<div className="mt-5 flex flex-wrap items-center justify-center gap-2">
					{suggestions.map((s, i) => (
						<button
							key={i}
							onClick={() => send(s)}
							disabled={isStreaming}
							className="cursor-pointer rounded-full border border-border/60 bg-background/50 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-border hover:text-foreground"
						>
							{s}
						</button>
					))}
				</div>
			)}
		</div>
	)
}
